
import { DtoKeys, Enums } from '@bode-canada/core';
import Typography from '@/ui-kit/components/typography';
import { TypographyProperty, TypographyType } from '@/ui-kit/components/typography/enums';
import {
  StyleColorSchemeType,
  StyleFontWeightType,
  StylePositionType,
  StyleSizeType,
} from '@/ui-kit/styles/enums';
import { ButtonFillType, ButtonProperty, ButtonPropsEventType } from '@/ui-kit/components/button/enums';
import { GutterProperty } from '@/ui-kit/components/gutter/enums';
import { ControlType } from '@/ui-kit/components/form/controls/enums';
import { LayoutItemColumn, LayoutItemProperty } from '@/ui-kit/components/layout-grid/item/enums';
import { LayoutContainerProperty } from '@/ui-kit/components/layout-grid/container/enums';
import LayoutGridContainer from '@/ui-kit/components/layout-grid/container';
import LayoutGridItem from '@/ui-kit/components/layout-grid/item';
import createMarketDataChartForm, {
  MarketDataChartFormControlName,
} from '@/components/features/market-data/components/chart/MarketDataChartControls';
import Form from '@/ui-kit/components/form';
import FormField from '@/ui-kit/components/form/form-field';
import { PROVINCES } from '@/constants';
import Gutter from '@/ui-kit/components/gutter';
import Button from '@/ui-kit/components/button';
import { SelectProperty } from '@/ui-kit/components/form/controls/components/select/enums';
import { InputFillType } from '@/ui-kit/components/form/controls/shared/input/enums';
import { CHART_LABEL } from '@/components/page-specific/market-data/constants';
import { CHART_SOLD_YEARS } from '@/constants/MarketResearchConstants';
import { DEFAULT_DATE_PERIOD } from '@/components/features/sold-data/shared/components/forms/date-period/constants';
import {
  MarketDataFilterFormControlName,
} from '@/components/features/market-data/components/filters/MarketDataFiltersFormControls';
import ChartsFactory from '@/components/page-specific/market-data/charts';
import { track } from '@/utils/analytics';
import { MarketDataEvent } from '@/constants/AnalyticEvents';
import LoadingSpinner from '@/components/LoadingSpinner';
import DashboardApiService from '@/services/api/serverless/dashboard';
import FilterMapper from '@/components/page-specific/market-data/mappers/filters';
import MarketDataApiService from '@/services/api/serverless/market-data';
import ModalService from '@/ui-kit/components/modal/ModalService';
import MarketDataShareModal from '@/components/page-specific/market-data/share/modal';
import ToastService from '@/components/toast/toast-service';
import { ToastMessage } from '@/ui-kit/components/toast/enums';
import { FormEventType, FormFieldProperty } from '@/ui-kit/components/form/enums';
import OptionsHelper from '@/utils/OptionsHelper';

export default {
  name: 'MarketDataChart',
  components: {
    Typography,
    Form,
    LayoutGridContainer,
    LayoutGridItem,
    FormField,
    Gutter,
    Button,
    LoadingSpinner,
    apexcharts: () => (process.client ? import('vue-apexcharts') : null),
  },
  props: {
    summaryParams: {
      type: Object,
      default: null,
    },
    province: {
      type: String,
      default: PROVINCES.AB,
    },
  },
  data() {
    return {
      formGroup: createMarketDataChartForm(),
      chartWidth: '100%',
      chartHeight: '400px',
      chartParams: null,
      chartData: null,
      chartSeries: null,
      loadingGraph: false,

      TypographyProperty,
      TypographyType,
      StyleFontWeightType,
      LayoutContainerProperty,
      StyleSizeType,
      LayoutItemProperty,
      LayoutItemColumn,
      ControlType,
      MarketDataChartFormControlName,
      StylePositionType,
      GutterProperty,
      ButtonProperty,
      ButtonPropsEventType,
      ButtonFillType,
      StyleColorSchemeType,
      FormEventType,
      FormFieldProperty,
    };
  },
  computed: {
    chartTypeValue() {
      return this.formGroup.getControl(MarketDataChartFormControlName.ChartType).value;
    },
    chartTypeInputProps() {
      return {
        [SelectProperty.Name]: MarketDataChartFormControlName.ChartType,
        [SelectProperty.FillType]: InputFillType.Underlined,
        [SelectProperty.Label]: 'Chart Type',
        [SelectProperty.Options]: OptionsHelper.mapValuesIntoOptions(
          Object.values(CHART_LABEL)
            .filter((label) => label !== CHART_LABEL[Enums.ChartTypeEnum.WEEKLY_VIEWS]),
        ),
      };
    },

    transactionDateControl() {
      return this.formGroup.getControl(MarketDataChartFormControlName.TransactionDate);
    },
    transactionDate() {
      return this.transactionDateControl.value;
    },
    transactionDateInputProps() {
      return {
        [SelectProperty.Name]: MarketDataChartFormControlName.TransactionDate,
        [SelectProperty.FillType]: InputFillType.Underlined,
        [SelectProperty.Label]: 'Time Range',
        [SelectProperty.Options]: OptionsHelper.mapValuesIntoOptions(CHART_SOLD_YEARS),
      };
    },
  },
  watch: {
    summaryParams: {
      immediate: true,
      async handler(value) {
        // NOTE: charts are only rendered on client, so fetch only on client
        if (process.client && value) {
          this.chartParams = {
            ...value,
            transactionDate: value[MarketDataFilterFormControlName.TransactionDate]
              || DEFAULT_DATE_PERIOD,
          };
          this.transactionDateControl.setValue(this.chartParams.transactionDate);

          await this.getChartData(this.chartParams, this.chartTypeValue);
        }
      },
    },
  },
  methods: {
    handleSubmit() {
      track(MarketDataEvent.GenerateGraphClick, {
        chartType: this.chartTypeValue,
        timeRange: this.transactionDate,
      });

      this.getNewGraph();
    },

    async getNewGraph() {
      this.chartParams.transactionDate = this.transactionDate;
      await this.getChartData(this.chartParams, this.chartTypeValue);
      this.wireHamburgerMenuButton();
    },

    wireHamburgerMenuButton() {
      const hamburgerMenuButton = document.querySelector('.apexcharts-menu-icon');
      if (hamburgerMenuButton) {
        hamburgerMenuButton.addEventListener('click', () => {
          const exportCSVButton = document.querySelector('.exportCSV');
          if (exportCSVButton) {
            exportCSVButton.addEventListener('click', () => {
              track('Market Data_Download CSV button clicked');
            });
          }
        });
      }
    },

    async getChartData(params, chartName) {
      this.loadingGraph = true;

      try {
        const chartData = chartName === CHART_LABEL[Enums.ChartTypeEnum.WEEKLY_VIEWS]
          ? await this.fetchListingChartData(params)
          : await this.fetchMarketChartData(chartName, params);

        this.chartData = ChartsFactory
          .getChartByType(chartData, this.chartWidth);
        this.chartSeries = chartData.charts;
      } finally {
        this.loadingGraph = false;
      }
    },

    async fetchListingChartData(params) {
      const chartData = await DashboardApiService.getListingViewsByListingId(params.listingId);

      return { ...chartData, chartType: Enums.ChartTypeEnum.WEEKLY_VIEWS };
    },

    async fetchMarketChartData(chartName, requestFilter) {
      const { closedAt, ...filters } = FilterMapper.mapToDto(requestFilter);
      const chartType = Object.entries(CHART_LABEL)
        // eslint-disable-next-line no-unused-vars
        .find(([_, label]) => chartName === label)?.[0];
      const timeStampKey = chartType === Enums.ChartTypeEnum.NUMBER_OF_NEW_LISTINGS
        ? DtoKeys.SearchListingFilterDtoKey.PUBLISHED_AT
        : DtoKeys.SearchListingFilterDtoKey.CLOSED_AT;
      const chartRequestBody = {
        filters: {
          ...filters,
          [timeStampKey]: closedAt,
        },
        chartType,
      };
      const chartData = await MarketDataApiService.getChartData(chartRequestBody);

      return { ...chartData, chartType };
    },

    async downloadGraphImage(graphName) {
      const { imgURI } = await this.$refs.marketDataChart.dataURI();

      // Create new image with the selected graph
      // And appened the graph title and footer to the final image.
      const img = new Image();
      img.src = imgURI;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width + 20;
        canvas.height = img.height + 160;
        ctx.fillStyle = '#FFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#000';
        ctx.font = '19pt sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(graphName, canvas.width / 2, 50);
        ctx.drawImage(img, 10, 60);
        ctx.font = '16pt sans-serif';
        ctx.fillText(
          'Visit bode.ca/market-data to research home trends in your neighborhood.',
          canvas.width / 2,
          canvas.height - 40,
        );
        this.forceFileDownload(canvas.toDataURL('image/jpeg'), graphName);
      };
    },
    forceFileDownload(data, chartType) {
      const link = document.createElement('a');
      link.href = data;
      link.setAttribute('download', `${chartType} Graph.jpeg`);
      document.body.appendChild(link);
      link.click();
    },
    async openShareSearchModal() {
      const shareSearchModal = await ModalService.open({
        component: MarketDataShareModal,
      });

      if (shareSearchModal) {
        ToastService.openPositive(ToastMessage.MarketDataShareSearchSuccess);
        track(MarketDataEvent.ShareSearchSendClick);
      }
    },
  },
};
